import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, X, AlertTriangle, Shield, Clock } from 'lucide-react';
import api, { resetAuthState } from './api';
import useGlobale from './global';
import { toast } from 'react-toastify';

const AuthModal = ({ isOpen, onClose, message, onSuccess }) => {
  const { login, forceLogoutViaModal } = useGlobale();
  const [credentials, setCredentials] = useState({
    username: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Réinitialiser le formulaire quand le modal s'ouvre
  useEffect(() => {
    if (isOpen) {
      setCredentials({ username: '', password: '' });
      setError('');
      setShowPassword(false);
      
      // Pré-remplir le nom d'utilisateur si disponible
      try {
        const savedCredentials = localStorage.getItem('credentials');
        if (savedCredentials) {
          const { username } = JSON.parse(savedCredentials);
          if (username) {
            setCredentials(prev => ({ ...prev, username }));
          }
        }
      } catch (e) {
        // Ignore les erreurs de parsing
      }
    }
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCredentials({
      ...credentials,
      [name]: value
    });
    setError('');
  };

  const validateForm = () => {
    if (!credentials.username.trim()) {
      setError("Le nom d'utilisateur est requis");
      return false;
    }
    if (!credentials.password) {
      setError("Le mot de passe est requis");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    setError('');

    try {
      const response = await api.post('/utilisateurs/signin/', {
        username: credentials.username,
        password: credentials.password
      });

      const { user, tokens } = response.data;

      // Réinitialiser l'état d'authentification
      resetAuthState();
      
      // Connecter l'utilisateur
      await login(user, tokens);
      
      toast.success('Reconnexion réussie ! Vous pouvez continuer.');
      
      // Exécuter le callback de succès si fourni
      if (onSuccess) {
        onSuccess();
      }
      
      // Fermer le modal
      onClose();
      
    } catch (err) {
      if (err.response?.status === 401) {
        setError("Identifiants incorrects. Veuillez vérifier votre nom d'utilisateur et mot de passe.");
      } else if (err.response?.status === 403) {
        setError("Votre compte est désactivé. Veuillez contacter l'administrateur.");
      } else if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError("Une erreur s'est produite. Veuillez réessayer.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    // Utiliser la fonction du store global qui gère la déconnexion complète
    forceLogoutViaModal();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity"></div>
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-md transform rounded-2xl bg-white shadow-2xl transition-all">
          
          {/* Header avec gradient */}
          <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-4 rounded-t-2xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/20 backdrop-blur-sm">
                    <Clock className="h-5 w-5 text-white" />
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Session expirée</h3>
                  <p className="text-sm text-amber-100">Reconnectez-vous pour continuer</p>
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="rounded-lg p-1 text-white/80 hover:bg-white/20 hover:text-white transition-colors"
                title="Fermer et aller à la page de connexion"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Message d'alerte */}
          <div className="px-6 py-4 bg-amber-50 border-b border-amber-100">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
              </div>
              <div>
                <p className="text-sm text-amber-800 font-medium">
                  Votre session a expiré pendant que vous travailliez
                </p>
                <p className="text-sm text-amber-700 mt-1">
                  {message || "Reconnectez-vous rapidement pour ne pas perdre votre travail en cours."}
                </p>
              </div>
            </div>
          </div>

          {/* Formulaire */}
          <div className="px-6 py-6">
            {error && (
              <div className="mb-4 bg-red-50 text-red-600 p-3 rounded-lg text-sm border border-red-100">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Nom d'utilisateur */}
              <div>
                <label htmlFor="modal-username" className="block text-sm font-medium text-gray-700 mb-1">
                  Nom d'utilisateur <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <input
                    id="modal-username"
                    name="username"
                    type="text"
                    required
                    value={credentials.username}
                    onChange={handleChange}
                    className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all duration-200"
                    placeholder="Nom d'utilisateur"
                    autoFocus
                  />
                </div>
              </div>

              {/* Mot de passe */}
              <div>
                <label htmlFor="modal-password" className="block text-sm font-medium text-gray-700 mb-1">
                  Mot de passe <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <input
                    id="modal-password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    required
                    value={credentials.password}
                    onChange={handleChange}
                    className="w-full pl-9 pr-11 py-2.5 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all duration-200"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors duration-200"
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              {/* Boutons */}
              <div className="flex space-x-3 pt-2">
                <button
                  type="button"
                  onClick={handleLogout}
                  className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-all duration-200 font-medium"
                >
                  Aller à la connexion
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className={`flex-1 px-4 py-2.5 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white transition-all duration-200 ${
                    loading 
                      ? 'bg-amber-400 cursor-not-allowed' 
                      : 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 transform hover:scale-[1.02]'
                  }`}
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Connexion...
                    </>
                  ) : (
                    <>
                      <Shield className="h-4 w-4 mr-2 inline" />
                      Se reconnecter
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-gray-50 rounded-b-2xl border-t border-gray-100">
            <div className="flex items-center space-x-2 text-xs text-gray-500">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
              <span>Vos données de formulaire seront préservées après la reconnexion</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthModal; 